import React, { useState } from "react";
import { apiFetch } from "../../services/api";
import LoadingButton from "../../components/common/LoadingButton";
import V2AuthLayout from "../components/V2AuthLayout";
import AuthConnectingStatus, {
  AUTH_CONNECTING_INITIAL,
} from "../components/AuthConnectingStatus";
import { useV2Theme } from "../theme/ThemeProvider";
import { signupErrorMessageFromResponse } from "../utils/signupErrorMessage";
import GoogleContinueButton from "../components/GoogleContinueButton";
import { continueWithGoogle, persistAuthSession } from "../auth/googleSso";
import {
  DEFAULT_ORGANIZATION_SIZE,
  displayNameFromEmail,
} from "../utils/signupDefaults";

const inputClass =
  "mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500/25";

export default function V2SignupPage({
  onLogin,
  onEmailSent,
  onSignupAuthenticated,
  onNeedsGoogleSignup,
}) {
  const { tokens } = useV2Theme();
  const [formData, setFormData] = useState({
    email: "",
    organization_name: "",
    password: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      setLoading(false);
      return;
    }
    if (formData.password.length > 72) {
      setError("Password is too long. Please use a password with 72 characters or less.");
      setLoading(false);
      return;
    }
    if (!termsAccepted) {
      setError("You must accept the Terms and Privacy Policy to create an account");
      setLoading(false);
      return;
    }

    const now = new Date().toISOString();
    try {
      const response = await apiFetch("/api/auth/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: displayNameFromEmail(formData.email),
          email: formData.email.trim(),
          organization_name: formData.organization_name.trim(),
          organization_size: DEFAULT_ORGANIZATION_SIZE,
          password: formData.password,
          terms_accepted: true,
          gdpr_consent: true,
          terms_accepted_at: now,
          gdpr_consent_at: now,
        }),
        timeout: 60000,
      });

      if (response.ok) {
        const data = await response.json();
        if (!data.user?.email_verified) {
          const email = data.user?.email || formData.email;
          onEmailSent?.(email, data.verification_email_sent === true);
        } else {
          localStorage.setItem("access_token", data.access_token);
          localStorage.setItem("user", JSON.stringify(data.user));
          onSignupAuthenticated?.();
        }
      } else {
        setError(await signupErrorMessageFromResponse(response));
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError("");
    const orgName = (formData.organization_name || "").trim();
    const extra =
      orgName && termsAccepted
        ? {
            organization_name: orgName,
            organization_size: DEFAULT_ORGANIZATION_SIZE,
            terms_accepted: true,
            gdpr_consent: true,
            terms_accepted_at: new Date().toISOString(),
            gdpr_consent_at: new Date().toISOString(),
          }
        : {};

    try {
      const result = await continueWithGoogle(extra);
      if (result.ok) {
        persistAuthSession(result.data);
        onSignupAuthenticated?.();
        return;
      }
      if (result.needsSignupDetails) {
        onNeedsGoogleSignup?.({
          idToken: result.idToken,
          email: result.email,
          displayName: result.displayName,
        });
        return;
      }
      setError(result.message || "Google sign-in failed");
    } finally {
      setGoogleLoading(false);
    }
  };

  const linkBtn = "text-sm font-medium text-blue-700 hover:text-blue-600";
  const busy = loading || googleLoading;

  return (
    <V2AuthLayout
      title="Create your account"
      subtitle="Start your Architecture Decision Workspace trial"
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">
            Email *
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            className={inputClass}
            placeholder="you@organization.com"
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
          />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-slate-700">
            Password *
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            minLength={8}
            maxLength={72}
            className={inputClass}
            placeholder="8–72 characters"
            value={formData.password}
            onChange={handleChange}
            autoComplete="new-password"
          />
        </div>
        <div>
          <label htmlFor="organization_name" className="block text-sm font-medium text-slate-700">
            Organisation name *
          </label>
          <input
            id="organization_name"
            name="organization_name"
            type="text"
            required
            className={inputClass}
            placeholder="Company or team"
            value={formData.organization_name}
            onChange={handleChange}
            autoComplete="organization"
          />
        </div>

        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            required
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
          />
          <span>
            I accept the{" "}
            <a
              href="https://decisionpen.com/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-600"
            >
              Terms and Conditions
            </a>{" "}
            and{" "}
            <a
              href="https://decisionpen.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-700 underline hover:text-blue-600"
            >
              Privacy Policy
            </a>{" "}
            *
          </span>
        </label>

        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        ) : null}

        <LoadingButton
          type="submit"
          loading={loading}
          loadingLabel={AUTH_CONNECTING_INITIAL}
          invert
          disabled={busy}
          className={`w-full ${tokens.button.primary} disabled:opacity-50`}
        >
          Create account
        </LoadingButton>

        <GoogleContinueButton
          onClick={handleGoogle}
          loading={googleLoading}
          disabled={busy}
          label="Continue with Google"
          loadingLabel={AUTH_CONNECTING_INITIAL}
        />

        <AuthConnectingStatus active={busy} />

        <p className="text-center text-sm text-slate-600">
          Already have an account?{" "}
          <button type="button" onClick={onLogin} className={linkBtn}>
            Sign in
          </button>
        </p>
      </form>
    </V2AuthLayout>
  );
}
